import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { notifyMethod } from '../../../App'
import useMandalDetails from '../../../hooks/useMandalDetails'
import useRedux from '../../../hooks/useRedux'
import useTranslations from '../../../hooks/useTranslations'
import { setFiscalYear } from '../../../redux/app/reducer'
import { setJobActiveTab } from '../../../redux/jobs/reducer'
import { userWiseRole } from '../../../utils/constant'
import { downloadReport } from '../../../utils/customFunctions'
import debounce from '../../../utils/debounce'
import { EVMasterSheet, RefurbishmentRequest } from '../../../utils/icons'
import { include, isEqual, notEqual, values } from '../../../utils/javascript'
import { getItem } from '../../../utils/localstorage'
import {
  getCurrentWeekDateRange,
  isSameDateRange,
} from '../../../utils/weekDateUtils'
import { disAssociateApi } from '../../userManagement/user.api'
import {
  addJobPostApi,
  generateMasterSheetApi,
  getJobDetailApi,
  getJobListApi,
  searchJobListApi,
} from '../jobs.api'
import {
  columnKeys,
  exportExcelOptions,
  getJobTabList,
  jobTypeRoleSearchBy,
  payloadType,
  searchByKeys,
  searchByLabels,
  tabKeys,
} from '../jobs.description'

const jobs = ({ userView = false, userId, userJobType } = {}) => {
  const mandalDetails = useMandalDetails()
  const { inspectionOfficer } = userWiseRole
  const { t } = useTranslations()
  const { dispatch, selector } = useRedux()
  const isDesktop = selector(state => state.app.isDesktop)
  const { value: fiscalYear, dateRange } = selector(
    state => state?.app?.fiscalYear,
  )
  const activeTab = selector(state => state?.jobs?.activeTab)
  const { type, status } = { ...activeTab }
  const [searchBy, setSearchBy] = useState(searchByKeys.mandal)
  const [columnFilters, setColumnFilters] = useState([])
  const [columnExportToExcel, setColumnExportToExcel] = useState([
    'job_SNo',
    'job_DateOfReceiptOfVehicle',
    'job_RVSFVehicleNo',
    'user_Type',
    'job_Source',
    'user_ProducerName',
    'job_CustomerName',
    'job_VehicleNo',
    'job_ManufacturingDate',
    'job_RegistrationDate',
    'job_ELVModel',
    'job_Category',
  ])
  const [viewRequestsModal, setViewRequestsModal] = useState({ open: false })
  const [masterSheetLoader, setMasterSheetLoader] = useState(false)

  const searchVal = useRef(null)
  const inspectionWeekInitializedRef = useRef(false)
  const searchDraftRef = useRef('')
  const [searchInput, setSearchInput] = useState('')
  const userDetails = JSON.parse(getItem('userData'))
  const { roleId } = { ...userDetails }
  const [data, setData] = useState({})
  const [jobModel, setJobModel] = useState({
    open: false,
    loader: false,
    data: null,
  })
  const [loading, setLoading] = useState(false)
  const { type: jobType } = { ...activeTab }
  const [selectExportColModal, setSelectExportColModal] = useState({
    open: false,
    selectedColumn: [
      'S. No.',
      'Date of Receipt of Vehicle',
      'Scrapping Facility Vehicle No.',
      'Type',
      'Source',
      'Producer Name',
      'Customer Name',
      'Vehicle No.',
      'Manufacturing Date',
      'Registration Date',
      'ELV Model',
    ],
  })
  const [disAssociateHostel, setDisAssociateHostel] = useState({
    open: false,
    data: null,
  })
  const [revertJobModal, setRevertJobModal] = useState({
    open: false,
    data: null,
    loader: false,
  })
  const [helpModal, setHelpModal] = useState(false)

  const onExportToExcel = async () => {
    setLoading(true)
    const report = isEqual(tabKeys.smeltingRequest, jobType)
      ? RefurbishmentRequest
      : EVMasterSheet
    report && (await downloadReport(report))
    setLoading(false)
  }

  const jobFilter = () => {
    return true
  }

  const tabList = getJobTabList(roleId)
  tabList.type = isEqual(status, tabKeys.unassignHostel)
    ? []
    : tabList.type.filter(info => jobFilter({ info }))

  useEffect(() => {
    if (isEqual(status, tabKeys.unassignHostel)) {
      // When unassign hostel tab is selected, clear the type
      if (type !== null) {
        dispatch(
          setJobActiveTab({
            type: null,
          }),
        )
      }
    } else if (
      !tabList.type?.some(
        ({ key, disabled }) => isEqual(key, type) && !disabled,
      )
    ) {
      dispatch(
        setJobActiveTab({
          status: status || tabList?.status?.[0]?.key,
          type: tabList?.type?.find(({ disabled }) => !disabled)?.key,
        }),
      )
    }
    if (type) {
      setColumnFilters(currentColumns)
    }
  }, [type, status])

  useEffect(() => {
    if (!include(currentSearchBy, searchBy)) {
      setSearchBy(currentSearchBy?.[0] || searchByKeys.jobId)
    }
    if (status) {
      setColumnFilters(currentColumns)
    }
  }, [status, type])

  useEffect(() => {
    if (userView || !fiscalYear || !isEqual(type, tabKeys.inspection)) return

    const currentWeekRange = getCurrentWeekDateRange()
    if (inspectionWeekInitializedRef.current) return
    if (isSameDateRange(dateRange, currentWeekRange)) {
      inspectionWeekInitializedRef.current = true
      return
    }

    inspectionWeekInitializedRef.current = false
    dispatch(
      setFiscalYear({
        dateRange: {
          ...dateRange,
          ...currentWeekRange,
        },
      }),
    )
  }, [dispatch, fiscalYear, type, dateRange?.from, dateRange?.to])

  useEffect(() => {
    if (
      !fiscalYear ||
      userView ||
      !type ||
      isEqual(status, tabKeys.unassignHostel)
    )
      return
    if (
      isEqual(type, tabKeys.inspection) &&
      !inspectionWeekInitializedRef.current
    ) {
      return
    }
    apiCall()
  }, [fiscalYear, status, type, dateRange?.from, dateRange?.to])

  useEffect(() => {
    if (fiscalYear && userView) {
      apiCall()
    }
  }, [userView])

  useEffect(() => {
    if (userView || !isEqual(type, tabKeys.inspection)) return undefined

    let timeoutId
    const scheduleNextDateRefresh = () => {
      const now = new Date()
      const nextDate = new Date(now)
      nextDate.setHours(24, 0, 0, 0)
      timeoutId = window.setTimeout(() => {
        const currentWeekRange = getCurrentWeekDateRange()
        inspectionWeekInitializedRef.current = false
        dispatch(
          setFiscalYear({
            dateRange: {
              ...dateRange,
              ...currentWeekRange,
            },
          }),
        )
        scheduleNextDateRefresh()
      }, nextDate.getTime() - now.getTime())
    }

    scheduleNextDateRefresh()

    return () => window.clearTimeout(timeoutId)
  }, [dispatch, userView, type, dateRange])
  useEffect(() => {
    if (searchVal.current) {
      apiCall()
    }
  }, [searchBy])

  const currentColumns = useMemo(() => {
    const {
      status: jobStatus,
      hostel: hostelCol,
      hostelContact: hostelContactCol,
      inspectionOfficer: inspectionOfficerCol,
      mandal,
      createdDate,
      completionDate,
      designation,
    } = columnKeys
    return [
      hostelCol,
      mandal,
      hostelContactCol,
      inspectionOfficerCol,
      ...(isEqual(type, tabKeys.inspection) && isEqual(status, tabKeys.complete)
        ? [completionDate]
        : [createdDate]),
      jobStatus,
      designation,
    ]
  }, [type, status, roleId])

  const currentSearchBy = useMemo(() => {
    const { jobId } = searchByKeys
    const searchValues = jobTypeRoleSearchBy(jobType, roleId)

    return (
      searchValues || [
        jobId,
        // For dynamic searchBy by role or active tab
        // ...ternary(isEqual(roleId, userWiseRole.admin), ['extra'], []),
        // ...ternary(isEqual(type, tabKeys.quote), ['extra2'], []),
      ]
    )
  }, [type, roleId])

  const columnFilterOptions = [columnKeys.all, ...values(currentColumns)].map(
    value => ({
      label: t(value),
      value: value,
    }),
  )

  const searchByOptions = values(currentSearchBy).map(value => ({
    label: t(searchByLabels[value]),
    value: value,
  }))

  const apiCall = async (pageNo = 1, range) => {
    if (!userView && isEqual(status, tabKeys.unassignHostel)) return

    const selectedType = userView ? userJobType : type
    const jobType = payloadType[selectedType]
    if (!jobType) return

    const isInspectionJob = isEqual(selectedType, tabKeys.inspection)
    const selectedDateRange = range || dateRange
    const shouldUseDateRange =
      (isInspectionJob || range) &&
      selectedDateRange?.from &&
      selectedDateRange?.to

    const params = {
      jobType: jobType,
      fiscalYear,
      active: isEqual(status, tabKeys.active),
      ...(userView && { userId }),
    }
    setData(pre => ({
      ...pre,
      [userJobType || type]: { ...pre?.[userJobType || type], loader: true },
    }))
    let resp
    if (searchVal.current || shouldUseDateRange) {
      if (searchVal.current) {
        params.searchTag = searchBy
        params.search = searchVal.current
      }

      if (shouldUseDateRange) {
        params.fromDate = selectedDateRange?.from
        params.toDate = selectedDateRange?.to
      }
      resp = await searchJobListApi({ params, pageNo })
    } else {
      resp = await getJobListApi({ params, pageNo })
    }
    setData(pre => ({
      ...pre,
      [userJobType || type]: {
        ...resp?.data,
        loader: false,
      },
    }))
  }
  const apiCallRef = useRef(apiCall)
  apiCallRef.current = apiCall

  const debouncedSearch = useMemo(
    () =>
      debounce(() => {
        const v = (searchDraftRef.current || '').trim()
        searchVal.current = v || null
        apiCallRef.current()
      }, 600),
    [],
  )

  const viewApiCall = useCallback(
    async jobId => {
      const response = await getJobDetailApi({
        params: {
          id: jobId,
          jobType: payloadType[type] || jobType,
        },
      })
      setJobModel(pre => ({ ...pre, loader: false, data: response?.data }))
    },
    [type, status],
  )

  const handleTableChange = pagination => {
    apiCall(pagination.current)
  }

  const onSearch = e => {
    const raw =
      typeof e?.target === 'string' || typeof e?.target === 'number'
        ? String(e.target)
        : (e?.target?.value ?? '')
    searchDraftRef.current = raw
    setSearchInput(raw)
    debouncedSearch()
  }

  const handleTabChange = tab => {
    if (tab?.status) {
      if (notEqual(tab?.status, tabKeys.active)) {
        const updatedFilters = columnFilters.filter(val =>
          notEqual(val, columnKeys.activeDays),
        )
        setColumnFilters(updatedFilters)
      } else {
        setColumnFilters(pre => [...pre, columnKeys.activeDays])
      }
    }
    dispatch(setJobActiveTab(tab))
  }

  const handleSearchByChange = value => {
    setSearchBy(value)
    searchDraftRef.current = ''
    setSearchInput('')
    searchVal.current = null
    apiCall(1)
  }

  const handleColumnFilterChange = values => {
    let newValues = values
    if (include(values, columnKeys.all)) {
      newValues = currentColumns
    }
    setColumnFilters(newValues)
  }

  const handleColumnExcelChange = values => {
    setColumnExportToExcel(values)
  }

  const handleViewClick = (jobId, jobType) => {
    setJobModel({ open: true, loader: true })
    setData(pre => ({
      ...pre,
      [type]: {
        ...pre?.[type],
        list: pre?.[type]?.list?.map(value =>
          isEqual(value.jobId, jobId) && !value.read
            ? { ...value, read: true }
            : value,
        ),
        loader: false,
      },
    }))
    viewApiCall(jobId, jobType)
  }

  const handleCloseModel = () =>
    setJobModel({ ...jobModel, open: false, data: null })

  const searchByProps = {
    value: searchBy,
    options: searchByOptions,
    onChange: handleSearchByChange,
  }

  const exportExcelList = exportExcelOptions.map(value => ({
    label: t(value?.label),
    value: value?.value,
  }))

  const exportExcelProps = {
    mode: 'multiple',
    value: columnExportToExcel?.map(value => t(value)),
    options: exportExcelList,
    onChange: handleColumnExcelChange,
    maxTagCount: 'responsive',
  }

  const columnFilterProps = {
    mode: 'multiple',
    value: columnFilters,
    options: columnFilterOptions,
    onChange: handleColumnFilterChange,
  }

  const checkEditPermission = jobStatus => {
    if (isEqual(status, tabKeys.complete)) return false
    switch (type) {
      case tabKeys.inspection:
        return include([inspectionOfficer], roleId)

      default:
        return false
    }
  }

  const onSelectExportCol = () => {
    setSelectExportColModal(prev => ({ ...prev, open: !prev.open }))
  }

  const onSelectColumn = list => {
    setSelectExportColModal(prev => ({ ...prev, selectedColumn: list }))
  }

  const onSelectAllColumn = e => {
    setSelectExportColModal(prev => ({
      ...prev,
      selectedColumn: e.target.checked
        ? exportExcelOptions?.map(value => value?.value)
        : [],
    }))
  }

  const searchSelectOptions = isEqual(searchBy, searchByKeys.mandal)
    ? mandalDetails
    : []

  const handleDisAssociateModal = ({ rowData }) => {
    setDisAssociateHostel({ open: !disAssociateHostel?.open, data: rowData })
  }

  const handleConfirmDisAssociate = async () => {
    const payloadData = `?userId=${disAssociateHostel?.data?.userId}&disAssociateUserId=${disAssociateHostel?.data?.hostelInfo?.id}`
    const { data } = await disAssociateApi({ params: payloadData })
    if (data?.success) {
      notifyMethod.success({
        message: 'msg_UserDisAssociatedSuccessfully',
      })
      apiCall(1)
    }
    setDisAssociateHostel({ open: false, data: null })
  }

  const handleRevertJobModal = ({ rowData } = {}) => {
    setRevertJobModal({
      open: !revertJobModal?.open,
      data: rowData || null,
      loader: false,
    })
  }

  const handleConfirmRevertJob = async () => {
    const payload = {
      id: revertJobModal?.data?.id,
      jobType: payloadType[userView ? userJobType : type],
      actionType: 'DELETE_ADD',
    }
    setRevertJobModal(prev => ({ ...prev, loader: true }))
    try {
      const response = await addJobPostApi({ payload })
      if (response?.data?.data) {
        await apiCall(data?.[userView ? userJobType : type]?.pageNo || 1)
        notifyMethod.success({
          message: 'msg_JobRevertedSuccessfully',
        })
      }
    } finally {
      setRevertJobModal({ open: false, data: null, loader: false })
    }
  }

  const handleFloatModal = () => {
    setHelpModal(!helpModal)
  }

  const generateMasterSheet = async () => {
    const payload = {
      type: payloadType[
        isEqual(activeTab.status, tabKeys.active)
          ? 'activeInspectionSheetMultiple'
          : 'inspectionSheetMultiple'
      ],
      fromDate: dateRange?.from,
      toDate: dateRange?.to,
    }
    setMasterSheetLoader(true)
    const response = await generateMasterSheetApi({ payload })
    if (response?.data?.success) {
      notifyMethod.success({
        message: isEqual(activeTab.status, tabKeys.active)
          ? 'msg_PendingListGeneratedSuccessfully'
          : 'msg_MasterSheetGeneratedSuccessfully',
      })
    }
    setMasterSheetLoader(false)
  }

  const handleViewRequestModal = () => {
    setViewRequestsModal(prev => ({ ...prev, open: !prev.open }))
  }

  return {
    t,
    data: userView ? data[userJobType] : data[type],
    tabList,
    jobModel,
    isDesktop,
    activeTab,
    columnFilters,
    searchByProps,
    searchInput,
    columnFilterProps,
    searchSelectOptions,
    apiCall,
    onSearch,
    handleViewClick,
    handleCloseModel,
    handleTabChange,
    handleTableChange,
    checkEditPermission,
    loading,
    onExportToExcel,
    exportExcelProps,
    onSelectExportCol,
    selectExportColModal,
    onSelectColumn,
    onSelectAllColumn,
    disAssociateHostel,
    handleDisAssociateModal,
    handleConfirmDisAssociate,
    revertJobModal,
    handleRevertJobModal,
    handleConfirmRevertJob,
    helpModal,
    handleFloatModal,
    generateMasterSheet,
    handleViewRequestModal,
    viewRequestsModal,
    masterSheetLoader,
  }
}

export default jobs
